import React, {useEffect, useState} from "react";
import {changeStatus, create, getById, update, deleteById} from "../../../services/cash-box.service";
import {NavigateFunction, NavLink, useNavigate, useParams} from "react-router-dom";
import * as Yup from 'yup';
import {useFormik} from 'formik';
import {
  AutocompleteInputChangeReason,
  Chip as ChipMaterial,
  Chip,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Button as MaterialButton,
  CircularProgress
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridSaveAltIcon,
  GridToolbarContainer
} from "@mui/x-data-grid";
import {ArrowBackIosNew} from "@mui/icons-material";
import showNotification from "../../utils/Notify";
import {Box, Breadcrumbs, Button, Grid, Stack, Typography} from "@mui/joy";
import {Experimental_CssVarsProvider as CssVarsProvider} from "@mui/material/styles";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {Autocomplete} from "@mui/lab";
import {findServiceOrders} from "../../../services/service-order.service";
import ICashBox from "../../../types/cash-box.type";
import {CashBoxStatus} from "../../../types/enumeration/CashBoxStatus";
import {CashBoxType} from "../../../types/enumeration/CashBoxType";
import {CashBoxTypeNames} from "../../../types/enumeration/CashBoxTypeNames";
import IUser from "../../../types/user.type";
import {getAllActiveByDepartment} from "../../../services/user.service";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment/moment";
import {Moment} from "moment";
import {CashBoxStatusColors} from "../../../types/enumeration/CashBoxStatusColors";
import {CashBoxStatusNames} from "../../../types/enumeration/CashBoxStatusNames";
import IMinimalServiceOrder from "../../../types/minimal-service-order.type";
import SendIcon from '@mui/icons-material/Send';
import ConfirmationModal from "../../utils/ConfirmationModal";
import IPurchase from "../../../types/purchase.type";
import {PurchaseApprovalStatusColors} from "../../../types/enumeration/PurchaseApprovalStatusColors";
import {PurchaseApprovalStatus} from "../../../types/enumeration/PurchaseApprovalStatus";
import {PurchaseApprovalStatusNames} from "../../../types/enumeration/PurchaseApprovalStatusNames";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import ICashBoxChangeStatus from "../../../types/cash-box-change-status.type";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import StatusHistoryModal from "../../utils/StatusHistoryModal";
import IAllocatedResource from "../../../types/allocated-resource.type";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import {exportPurchases} from "../../../services/purchase.service";
import DownloadIcon from "@mui/icons-material/Download";

export const CashBoxForm: React.FC = () => {
  let navigate: NavigateFunction = useNavigate();
  const { cashBoxId } = useParams();
  const [exporting, setExporting] = useState(false);
  const [selectedServiceOrders, setSelectedServiceOrders] = useState<any>(null);
  const [serviceOrders, setServiceOrders] = useState<Array<IMinimalServiceOrder>>([]);
  const [allUsers, setAllUsers] = useState<Array<IUser>>([]);
  const [users, setUsers] = useState<Array<IUser>>([]);
  const [selectedNewStatus, setSelectedNewStatus] = React.useState<CashBoxStatus | null>(null);
  const [confirmationModalOpen, setConfirmationModalOpen] = React.useState(false);
  const [modalTitle, setModalTitle] = React.useState("");
  const [modalMessage, setModalMessage] = React.useState("");
  const [statusHistoryModalOpen, setStatusHistoryModalOpen] = React.useState(false);
  const [confirmationDeleteModalOpen, setConfirmationDeleteModalOpen] = React.useState(false);

  const initialValues: ICashBox = {
    id: null,
    amount: 0,
    createdBy: null,
    assignedWorker: null,
    comments: "",
    created: null,
    serviceOrder: null,
    status: CashBoxStatus.IN_PROGRESS,
    type: CashBoxType.CASH,
    purchases: [],
    statusHistory: []
  };

  const [cashBox, setCashBox] = useState<ICashBox>(initialValues);

  const validationSchema = Yup.object({
    assignedWorker: Yup.object().required('El Recurso es requerido'),
    created: Yup.object().required('La Fecha es requerida'),
    amount: Yup.number().required('El Monto de la Caja  requerido').min(0, "El valor minimo es 0"),
  });

  const onSubmit = (cashBox: ICashBox) => {
    if (cashBoxId) {
      update(cashBox.id, cashBox)
        .then(() => {
          showNotification({type: 'success', message: 'Caja acutalidaza correctamente.'});
          if (cashBox.status === CashBoxStatus.IN_PROGRESS) {
            fetchCashBox()
          } else {
            goBack();
          }
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    } else {
      create(cashBox)
        .then((response) => {
          showNotification({type: 'success', message: 'Caja creada correctamente.'});
          if (cashBox.status === CashBoxStatus.IN_PROGRESS) {
            navigate('/cash-boxes/edit/' + response.data.id);
          } else {
            goBack();
          }
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit,
    validationSchema: validationSchema,
  });

  const goBack = () => {
    navigate("/cash-boxes");
  };

  const columns: GridColDef<IPurchase>[] = [
    {
      field: 'invoiceNumber',
      headerName: 'Factura Numero',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth: 100,
      flex: 1
    },
    {
      field: 'created',
      headerName: 'Fecha',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >{`${moment(params.row.created).format('DD/MM/YYYY')}`}</span>;
      }
    },
    {
      field: 'invoiceType',
      headerName: 'Tipo',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >{params.row.invoiceType?.name}</span>;
      }
    },
    {
      field: 'approvalStatus',
      headerName: 'Estado',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <Chip color={PurchaseApprovalStatusColors[PurchaseApprovalStatus[params.row.approvalStatus as keyof typeof PurchaseApprovalStatus]]}
                     label={PurchaseApprovalStatusNames[PurchaseApprovalStatus[params.row.approvalStatus as keyof typeof PurchaseApprovalStatus]]}
                     sx={{width: 160, fontWeight: 'bold', height: 20, marginBottom: 0.5}}/>;
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      headerAlign: 'center',
      align: 'center',
      minWidth: 20,
      flex: 1,
      cellClassName: 'actions',
      getActions: (params) => {
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(params.row)}
            color="inherit"
          />,
          // <GridActionsCellItem
          //   icon={<DeleteForeverIcon />}
          //   label="Delete"
          //   className="textPrimary"
          //   onClick={handleDeleteClick(params.row)}
          //   color="inherit"
          // />
        ];
      },
    },
  ];

  const handleEditClick = (purchase: IPurchase) => () => {
    navigate('purchase/edit/' + purchase.id);
  };

  const handleDeleteClick = () => () => {
    setConfirmationDeleteModalOpen(true);
  };

  const handleConfirmationDeleteModalClose = () => {
    setConfirmationDeleteModalOpen(false);
  }

  const onConfirmDelete = () => {
    deleteById(cashBoxId)
      .then(() => {
        showNotification({type: 'success', message: 'Liquidacion eliminada correctamente.'});
        handleConfirmationDeleteModalClose();
        goBack();
      })
      .catch(error => {
        showNotification({type: 'error', message: error.response.data.message});
      });
  };

  const handleStatusHistoryModalOpen = () => {
    setStatusHistoryModalOpen(true);
  }

  const handleStatusHistoryModalClose = () => {
    setStatusHistoryModalOpen(false);
  }

  const onConfirm = (comments?: string) => {
    if (selectedNewStatus) {
      let newStatus: ICashBoxChangeStatus = {
        status: selectedNewStatus,
        comments: comments ? comments : '',
      };
      changeStatus(cashBox.id, newStatus)
        .then((response) => {
          let message = newStatus.status === CashBoxStatus.SENT ? "La Caja fue Enviada correctamente." :
            newStatus.status === CashBoxStatus.TO_FIX ? "La Caja fue Enviada a Corregir correctamente."
              : "La Caja fue Cerrada correctamente.";
          showNotification({type: 'success', message: message});
          navigate('/cash-boxes')
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    }

    setSelectedNewStatus(null);
    setConfirmationModalOpen(false);
  };

  const handleConfirmationModalClose = () => {
    setSelectedNewStatus(null);
    setConfirmationModalOpen(false);
  }

  const handleOpenChangeStatusModal = (status: CashBoxStatus) => {
    if (status === CashBoxStatus.SENT) {
      setModalTitle("Enviar Caja");
      setModalMessage("Al confirmar se Enviara la Caja al Recurso asignado.")
    } else if (status === CashBoxStatus.TO_FIX) {
      setModalTitle("Corregir Caja");
      setModalMessage("Al confirmar se Enviara la Caja al Recurso asignado para ser corregida.")
    } else {
      setModalTitle("Cerrar Caja");
      setModalMessage("Al confirmar se Cerrara la Caja.")
    }
    setSelectedNewStatus(status);
    setConfirmationModalOpen(true);
  };

  const handleChangeCreatedDate = (value: Moment | null) => {
    formik.setFieldValue('created', value ? moment(value) : null)
  };

  const handleChangeType = (event: SelectChangeEvent) => {
    formik.setFieldValue('type', event.target.value);
  };

  const onInputChange = (event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
    if (value.length > 1) {
      findServiceOrders({keyword: value})
        .then( (response) => {
          setServiceOrders(response.data)
        })
    }
  };

  useEffect(() => {
    if (cashBox.serviceOrder?.id) {
      setSelectedServiceOrders(cashBox.serviceOrder);
      setServiceOrders([cashBox.serviceOrder as IMinimalServiceOrder]);
    }
  }, [cashBox.serviceOrder?.id]);

  const fetchUsers = async () => {
    getAllActiveByDepartment(null).then( (response) => {
      setAllUsers(response.data)
      setUsers(response.data)
    })
  };

  const fetchCashBox = async () => {
    if (cashBoxId) {
      getById(cashBoxId).then( (response) => {
        let cashBoxData = response.data;
        setCashBox(cashBoxData)
        formik.setValues({
          ...cashBoxData,
          created: moment(cashBoxData.created)
        });
      })
    }
  };

  const handleExportPurchases = async () => {
    try {
      setExporting(true);

      type Param = {[key: string] : string}
      const params: Param = {}

      if (cashBoxId !== undefined) {
        params.cashBoxId = cashBoxId;
      }

      await exportPurchases(params);
    } finally {
      setExporting(false);
    }
  };

  function EditToolbar() {
    return (
      <GridToolbarContainer sx={{paddingBottom: 1}}>
        <Box sx={{ flexGrow: 1 }} />
        <MaterialButton
          variant="outlined"
          color="success"
          endIcon={
            exporting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <DownloadIcon />
            )
          }
          onClick={handleExportPurchases}
        >
          Exportar Excel
        </MaterialButton>
      </GridToolbarContainer>
    );
  }

  useEffect(() => {
    fetchUsers()
  }, []);

  useEffect(() => {
    fetchCashBox();
  }, [cashBoxId]);

  return (
    <Box
      sx={{
        px: { xs: 2},
        pt: { xs: 'calc(12px + var(--Header-height))' },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100dvh',
        overflow: "auto"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: 1 },
              width: '100%',
            }}
          >
            <Breadcrumbs
              size="sm"
              aria-label="breadcrumbs"
              separator={<ChevronRightRoundedIcon/>}
              sx={{ pl: 0 }}
            >
              <Typography level="h4" component="h1">
                <LocalAtmIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} />
              </Typography>
              <Typography level="h4" component="h1">
                Control Financiero
              </Typography>
              <Typography level="h4" component="h1">
                <Link component={NavLink} to='/cash-boxes' underline={"none"}>Cajas</Link>
              </Typography>
              <Typography level="h4" component="h1">
                {cashBoxId ? "Editar" : "Nuevo"}
              </Typography>
            </Breadcrumbs>
            <Box>
              <CssVarsProvider>
                <ChipMaterial
                  color={CashBoxStatusColors[CashBoxStatus[formik.values.status as keyof typeof CashBoxStatus]]}
                  label={CashBoxStatusNames[CashBoxStatus[formik.values.status as keyof typeof CashBoxStatus]]}
                  sx={{width: 200, fontWeight: 'bold', fontSize: 15}}
                  icon={<ManageSearchIcon fontSize="medium" sx={{marginTop: -0.5}} />}
                  clickable={true}
                  onClick={() => handleStatusHistoryModalOpen()}
                />
              </CssVarsProvider>
            </Box>
          </Box>

          <ConfirmationModal
            open={confirmationModalOpen}
            onConfirm={(comments?: string) => { onConfirm(comments)}}
            onCancel={handleConfirmationModalClose}
            title={modalTitle}
            message={modalMessage}
            isDeleteAction={false}
            enableComments={true}
          />

          <StatusHistoryModal
            open={statusHistoryModalOpen}
            title={"Historial de Estados de Caja"}
            history={cashBox.statusHistory}
            onCancel={handleStatusHistoryModalClose}
            historyType={"cashBox"}
          />

          <ConfirmationModal
            open={confirmationDeleteModalOpen}
            onConfirm={onConfirmDelete}
            onCancel={handleConfirmationDeleteModalClose}
            title="Confirmacion"
            message="Esta seguro de eliminar esta Caja?"
            isDeleteAction={true}
          />

          <Box sx={{ width: '100%', typography: 'body1' }}>
            <form className="form" onSubmit={formik.handleSubmit}>
              <Grid
                container
                rowSpacing={2}
                columnSpacing={2}
                marginBlock={1}
                marginInline={0}
                sx={{ width: '100%' }}
              >
                <Grid xl={6} md={6} sm={6} xs={6} container rowSpacing={2} columnSpacing={2}>

                  <Grid xl={12} md={12} sm={12} xs={12}>
                    <CssVarsProvider>
                      <FormControl variant="filled" fullWidth>
                        <InputLabel id="role-label" shrink>Orden de Servicio</InputLabel>
                        <Autocomplete
                          id="serviceOrder-autocomplete"
                          value={selectedServiceOrders}
                          getOptionLabel={(serviceOrder) => `${serviceOrder.number}`}
                          onInputChange={onInputChange}
                          options={serviceOrders}
                          isOptionEqualToValue={(option, value) => option?.id === value?.id}
                          onChange={(e, value) => {
                            setSelectedServiceOrders(value);
                            formik.setFieldValue('serviceOrder', value);
                            if (value) {
                              setUsers(value.allocatedResources.map((ar: IAllocatedResource) => ar.worker as IUser));
                            } else {
                              setUsers(allUsers);
                            }
                          }}
                          fullWidth
                          readOnly={formik.values.status !== CashBoxStatus.IN_PROGRESS}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant={"filled"}
                              slotProps={{ inputLabel: {shrink: true, type: 'search'}}}
                              onBlur={formik.handleBlur}
                            />
                          )}
                        />
                        {formik.touched.serviceOrder && formik.errors.serviceOrder ? (
                          <span className="Error-alert">{String(formik.errors.serviceOrder)}</span>
                        ) : null}
                      </FormControl>
                    </CssVarsProvider>
                  </Grid>

                  <Grid xl={12} md={12} sm={12} xs={12}>
                    <CssVarsProvider>
                      <Autocomplete
                        id="assignedWorker"
                        value={formik.values.assignedWorker}
                        getOptionLabel={(user) =>
                          `${user.lastName || ''}, ${user?.name || ''}`
                        }
                        options={users}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        onChange={(e, value) => {
                          formik.setFieldValue('assignedWorker', value);
                        }}
                        fullWidth
                        readOnly={formik.values.status !== CashBoxStatus.IN_PROGRESS}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant={"filled"}
                            name="assignedWorker"
                            label="Recurso Asigado"
                            slotProps={{ inputLabel: {shrink: true}}}
                            onBlur={formik.handleBlur}
                            error={!!(formik.touched.assignedWorker && formik.errors.assignedWorker)}
                          />
                        )}
                      />
                      {formik.touched.assignedWorker && formik.errors.assignedWorker ? (
                        <span className="Error-alert">{String(formik.errors.assignedWorker)}</span>
                      ) : null}
                    </CssVarsProvider>
                  </Grid>

                  <Grid xl={12} md={12} sm={12} xs={12}>
                    <CssVarsProvider>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          name="start"
                          label="Fecha"
                          format="DD/MM/YYYY"
                          value={formik.values.created ? moment(formik.values.created) : null }
                          onChange={handleChangeCreatedDate}
                          readOnly={formik.values.status !== CashBoxStatus.IN_PROGRESS}
                          slotProps={{
                            textField: {
                              variant: 'filled',
                              fullWidth: true,
                              onChange: formik.handleChange,
                              onBlur: formik.handleBlur,
                              error: formik.touched.created && Boolean(formik.errors.created),
                              InputLabelProps: {
                                shrink:  true
                              }
                            }
                          }} />
                      </LocalizationProvider>
                      {formik.touched.created && formik.errors.created ? (
                        <span className="Error-alert">{formik.errors.created}</span>
                      ) : null}
                    </CssVarsProvider>
                  </Grid>

                  <Grid xl={12} md={12} sm={12} xs={12}>
                    <CssVarsProvider>
                      <FormControl variant="filled" fullWidth>
                        <InputLabel id="type-label" shrink>Tipo</InputLabel>
                        <Select
                          labelId="type-label"
                          id="type"
                          name="type"
                          label="Tipo"
                          value={formik.values.type}
                          onBlur={formik.handleBlur}
                          onChange={handleChangeType}
                          readOnly={formik.values.status !== CashBoxStatus.IN_PROGRESS}
                        >
                          {Object
                            .keys(CashBoxType)
                            .map((key) => (
                              <MenuItem key={key} value={CashBoxType[key as keyof typeof CashBoxType]}>
                                {CashBoxTypeNames[CashBoxType[key as keyof typeof CashBoxType]]}
                              </MenuItem>
                            ))}
                        </Select>
                        {formik.touched.type && formik.errors.type ? (
                          <span className="Error-alert">{String(formik.errors.type)}</span>
                        ) : null}
                      </FormControl>
                    </CssVarsProvider>
                  </Grid>

                  <Grid xl={6} md={6} sm={6} xs={6}>
                    <CssVarsProvider>
                      <TextField
                        fullWidth
                        id="amount"
                        label="Monto"
                        name="amount"
                        variant="filled"
                        type={"number"}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.amount}
                        error={!!(formik.touched.amount && formik.errors.amount)}
                        slotProps={{ inputLabel: {shrink: true}, input: {readOnly: formik.values.status !== CashBoxStatus.IN_PROGRESS}}}
                      />
                      {formik.touched.amount && formik.errors.amount ? (
                        <span className="Error-alert">{formik.errors.amount}</span>
                      ) : null}
                    </CssVarsProvider>
                  </Grid>

                  <Grid xl={6} md={6} sm={6} xs={6}>
                    <CssVarsProvider>
                      <Tooltip title={"Sumatoria de todas las compras cargadas a la caja."} placement="top">
                        <TextField
                          fullWidth
                          id="amount-surrendered"
                          label="Monto Consumido"
                          name="amount-surrendered"
                          variant="filled"
                          type={"number"}
                          value={cashBox.purchases?.reduce((sum, purchase) => sum + purchase.totalAmount, 0) ?? 0}
                          slotProps={{ inputLabel: {shrink: true}, input: {readOnly: true}}}
                        />
                      </Tooltip>
                    </CssVarsProvider>
                  </Grid>
                </Grid>

                <Grid xl={6} md={6} sm={6} xs={6} >
                  <CssVarsProvider>
                    <TextField
                      fullWidth
                      id="comments"
                      label="Comentarios"
                      name="comments"
                      variant="filled"
                      type={"text"}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.comments}
                      error={!!(formik.touched.comments && formik.errors.comments)}
                      slotProps={{ inputLabel: {shrink: true}, input: {readOnly: formik.values.status !== CashBoxStatus.IN_PROGRESS}}}
                      multiline
                      rows={14}
                      sx={{mb: '3px'}}
                    />
                    {formik.touched.comments && formik.errors.comments ? (
                      <span className="Error-alert">{formik.errors.comments}</span>
                    ) : null}
                  </CssVarsProvider>
                </Grid>
              </Grid>

              <Box sx={{ width: '100%', height: 384, typography: 'body1' }} ml={1} mb={2} pr={2}>
                <CssVarsProvider>
                  <DataGrid
                    slots={{toolbar: EditToolbar}}
                    rows={cashBox.purchases}
                    columns={columns}
                    rowHeight={25}
                    columnHeaderHeight={30}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    pageSizeOptions={[10]}
                    localeText={{ noRowsLabel: "No Hay Compras Registradas" }}
                    disableRowSelectionOnClick
                  />
                </CssVarsProvider>
              </Box>

              <Stack direction="row" justifyContent="space-between" spacing={2} pr={1} pl={1}>
                <Stack direction="row" justifyContent="flex-start" spacing={2}>
                  {cashBoxId && formik.values.status === CashBoxStatus.IN_PROGRESS &&
                    <Button
                        onClick={() => handleOpenChangeStatusModal(CashBoxStatus.SENT)}
                        variant="solid"
                        color="warning"
                        endDecorator={<SendIcon />}>
                        Enviar Caja
                    </Button>
                  }
                  {cashBoxId && formik.values.status === CashBoxStatus.IN_PROGRESS &&
                    <Button
                        onClick={handleDeleteClick()}
                        variant="solid"
                        color="danger"
                        endDecorator={<DeleteForeverIcon />}>
                        Eliminar Caja
                    </Button>
                  }
                  {cashBoxId && formik.values.status === CashBoxStatus.REPORT_SUBMITTED &&
                    <Button
                        onClick={() => handleOpenChangeStatusModal(CashBoxStatus.TO_FIX)}
                        variant="solid"
                        color="danger"
                        endDecorator={<ErrorOutlineIcon />}>
                        Corregir Caja
                    </Button>
                  }
                </Stack>

                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                  <Button onClick={() => goBack()} variant="outlined" endDecorator={<ArrowBackIosNew />}>Volver</Button>
                  {formik.values.status === CashBoxStatus.IN_PROGRESS &&
                    <Button type="submit" variant="solid" endDecorator={<GridSaveAltIcon />}>Guardar</Button>
                  }
                  {cashBoxId && formik.values.status === CashBoxStatus.REPORT_SUBMITTED &&
                    <Button
                      onClick={() => handleOpenChangeStatusModal(CashBoxStatus.CLOSE)}
                      variant="solid"
                      color="success"
                      endDecorator={<CheckCircleOutlineIcon />}
                    >
                      Cerrar Caja
                    </Button>
                  }
                </Stack>
              </Stack>

            </form>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};


